/**
 * Retrieval — Phase 2 permission-aware grounded Q&A (the demo centerpiece).
 *
 * Flow:
 *   query box -> POST /api/retrieval/query -> render
 *     1. a prominent EntitlementBanner (returned/candidates + withheld counts)
 *     2. the grounded answer (markdown) with numbered citations
 *     3. the citation list (permitted sources only)
 *
 * Switching personas (via the persona switcher / re-login) and re-running the
 * SAME query visibly changes the banner and the answer, because the backend
 * intersects each chunk's allowed_groups with the persona's entra_groups. The UI
 * never displays the title or content of a suppressed document — only counts.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { MARKDOWN_BLOCKED } from '../lib/markdownPolicy';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { EntitlementBanner } from '../components/EntitlementBanner';
import { SensitivityBadgeList } from '../components/SensitivityBadge';
import type {
  Citation,
  RetrievalQueryRequest,
  RetrievalQueryResponse,
} from '../lib/retrieval';

const EXAMPLE_QUERIES: readonly string[] = [
  'What are the ITAR handling requirements for Program X hardware?',
  'Summarize the latest supplier compliance reviews.',
  'What is our compensation philosophy?',
];

function CitationCard({
  citation,
  cited = false,
}: {
  citation: Citation;
  /** True when the grounded answer actually referenced this source. */
  cited?: boolean;
}): ReactNode {
  return (
    <li className="rounded-lg border border-steel-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="mr-1 font-mono text-xs text-navy-600">
            {citation.marker}
          </span>
          {citation.source_url ? (
            <a
              href={citation.source_url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sm font-medium text-navy-900 underline decoration-steel-300 underline-offset-2 hover:decoration-navy-900"
            >
              {citation.title ?? 'Untitled source'}
            </a>
          ) : (
            <span className="text-sm font-medium text-navy-900">
              {citation.title ?? 'Untitled source'}
            </span>
          )}
        </div>
        <span className="flex shrink-0 items-center gap-1">
          {cited && (
            <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
              cited
            </span>
          )}
          <SensitivityBadgeList sensitivity={citation.sensitivity} />
        </span>
      </div>
      {citation.snippet && (
        <p className="mt-1 text-xs leading-relaxed text-steel-600">
          {citation.snippet}
        </p>
      )}
      <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-steel-500">
        {citation.source_system && <span>{citation.source_system}</span>}
        {citation.site_or_drive && <span>{citation.site_or_drive}</span>}
      </div>
    </li>
  );
}

export function Retrieval(): ReactNode {
  const { user } = useAuth();
  const [query, setQuery] = useState('');

  const mutation = useMutation<RetrievalQueryResponse, ApiError, string>({
    mutationFn: (q: string) =>
      api.post<RetrievalQueryResponse>('/retrieval/query', {
        query: q,
      } satisfies RetrievalQueryRequest),
  });

  const result = mutation.data;

  function runQuery(q: string): void {
    const trimmed = q.trim();
    if (!trimmed) return;
    setQuery(trimmed);
    mutation.mutate(trimmed);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    runQuery(query);
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 animate-fade-in">
      <header>
        <h2 className="text-xl font-semibold text-navy-900">
          Permission-Aware Retrieval
        </h2>
        <p className="mt-1 text-sm text-steel-600">
          Ask a question across the Trident knowledge corpus. Answers are grounded
          only in documents the{' '}
          <span className="font-medium text-navy-700">
            {user?.role ?? 'current'}
          </span>{' '}
          role is entitled to see — the same query returns different results for
          different roles.
        </p>
      </header>

      <form onSubmit={onSubmit} className="card flex flex-col gap-3">
        <label htmlFor="retrieval-query" className="text-sm font-medium text-navy-900">
          Your question
        </label>
        <textarea
          id="retrieval-query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          rows={3}
          placeholder="e.g. What are the ITAR handling requirements for Program X?"
          className="w-full resize-y rounded-lg border border-steel-300 px-3 py-2 text-sm focus:border-navy-500 focus:outline-none focus:ring-1 focus:ring-navy-500"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLE_QUERIES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => runQuery(ex)}
                className="rounded-full border border-steel-200 bg-steel-50 px-2.5 py-1 text-xs text-steel-600 hover:bg-steel-100"
              >
                {ex}
              </button>
            ))}
          </div>
          <button
            type="submit"
            className="btn-primary disabled:opacity-50"
            disabled={mutation.isPending || query.trim().length === 0}
          >
            {mutation.isPending ? 'Searching…' : 'Ask'}
          </button>
        </div>
      </form>

      {mutation.isError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Something went wrong running your query.'}
        </div>
      )}

      {result && (
        <>
          <EntitlementBanner
            decision={result.decision}
            candidatesCount={result.candidates_count}
            returnedCount={result.returned_count}
            excludedCounts={result.excluded_counts}
            suppressionNotice={result.suppression_notice}
          />

          <article className="card">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-steel-500">
              Grounded answer
            </h3>
            <div className="prose prose-sm max-w-none text-navy-900">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                disallowedElements={MARKDOWN_BLOCKED}
              >
                {result.answer}
              </ReactMarkdown>
            </div>
          </article>

          {result.citations.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-steel-500">
                Sources considered ({result.citations.length})
              </h3>
              <p className="mb-2 text-xs text-steel-500">
                Every document you were permitted to see for this question. The
                ones marked &ldquo;cited&rdquo; are those the answer drew on.
              </p>
              <ul className="flex flex-col gap-2">
                {result.citations.map((c) => (
                  <CitationCard
                    key={c.chunk_id}
                    citation={c}
                    cited={result.answer.includes(c.marker)}
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default Retrieval;
